import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ModeEditTwoTone,
  AddCircleOutline,
  Settings,
  Close,
} from '@mui/icons-material';
import {
  type ApolloClient,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { useNavigate } from 'react-router-dom';
import { PANEL_FORM_MODE } from './constants';
import { PanelFormDialog } from './PanelFormDialog';
import { PANEL_FORM_DIALOG_TEXT, PANEL_LIST_TEXT } from '../../../lang';
import {
  useGetGSPanelList,
  useCreatePanel,
  useUpdatePanel,
  type PanelFormData,
  PANEL_STATUS,
  panelStatusLabel,
  useGetSinglePanel,
  useDeletePanel,
} from '../../../graphql/GSAssessment';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import { getOptionsLabel } from '../../../../Monitor/components';
import {
  Table,
  Alert,
  IconButton,
  Button,
  type AlertDetails,
  Loading,
  type HeadCell,
  Dialog,
} from '../../../../../components';
import { handlePageLoading } from '../../../../../utils';
import { panelOptionWithMixed } from '../../../../Nomination/components';

const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

export type PanelFormListData = PanelFormData & {
  _id: string;
  action?: React.ReactNode;
};

export const GSManagement: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const filter = {};
  const [panelDialogOpen, setPanelDialogOpen] = useState<boolean>(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [pageloading, setPageLoading] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const initialPanelFormData: PanelFormData = {
    title: '',
    fundingSource: '',
    entryYear: '',
    panelType: '',
    endDate: '',
    panelChair: '',
    tagOptions: [],
    panelMember: [],
    status: PANEL_STATUS.PREPARED,
    resultOptions: [],
  };

  const [panelFormMode, setPanelFormMode] = useState(PANEL_FORM_MODE.CREATE);
  const [panelFormData, setPanelFormData] =
    useState<PanelFormData>(initialPanelFormData);
  const [confirmDialogOpen, setConfirmPanelDialogOpen] =
    useState<boolean>(false);

  const deletePanelFormik = useFormik<{ panelId: string }>({
    initialValues: { panelId: '' },
    validationSchema: object({
      panelId: string(),
    }),
    onSubmit: () => {},
  });

  const {
    gsPanelList,
    fundingsNeedPanel,
    entryYears,
    loading,
    error: getGSApplicationsError,
    refetch,
  } = useGetGSPanelList(filter);

  const visibleData: PanelFormListData[] = gsPanelList;

  const dataCells: Array<HeadCell<PanelFormListData>> = [
    {
      id: 'fundingSource',
      label: PANEL_LIST_TEXT.FUNDING,
      isSortable: false,
      render: (row: PanelFormListData) =>
        fundingSourceValues[
          row.fundingSource as keyof typeof fundingSourceValues
        ],
    },
    {
      id: 'entryYear',
      label: PANEL_LIST_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: PanelFormListData) => row.entryYear,
    },
    {
      id: 'title',
      label: PANEL_LIST_TEXT.TITLE,
      isSortable: false,
      render: (row: PanelFormListData) => row.title,
    },
    {
      id: 'panelType',
      label: PANEL_LIST_TEXT.TYPE,
      isSortable: false,
      render: (row: PanelFormListData) =>
        getOptionsLabel(panelOptionWithMixed, row.panelType),
    },
    {
      id: 'endDate',
      label: PANEL_LIST_TEXT.MEETING_DATE,
      isSortable: false,
      render: (row: PanelFormListData) =>
        dayjs(row.endDate).format('DD/MM/YYYY'),
    },
    {
      id: 'status',
      label: PANEL_LIST_TEXT.STATUS,
      isSortable: false,
      render: (row: PanelFormListData) => panelStatusLabel[row.status],
    },
    {
      id: 'action',
      label: PANEL_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelFormListData) => action(row),
    },
  ];

  const action = (row: PanelFormListData): React.ReactNode => {
    const { _id, status } = row;
    const disabledDelete =
      status === PANEL_STATUS.COMPLETED || status === PANEL_STATUS.IN_PROGRESS;
    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          <IconButton
            title="Panel Detail"
            color="primary"
            onClick={handleOpenDetail(_id)}
            icon={<ModeEditTwoTone />}
          />
          <IconButton
            title="Panel Settings"
            onClick={async () => {
              await handleEditPanelInformation(_id);
            }}
            color="primary"
            icon={<Settings />}
          />
          <IconButton
            title="Delete"
            onClick={async () => {
              await handleOpenDeleteConfirm(_id);
            }}
            disabled={disabledDelete}
            color="primary"
            icon={<Close />}
          />
        </div>
      </div>
    );
  };
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleEditPanelInformation = async (panelId: string): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      try {
        const panelDetail = await useGetSinglePanel(client, panelId);
        setPanelFormMode(PANEL_FORM_MODE.UPDATE);
        setPanelFormData(panelDetail);

        setPanelDialogOpen(true);
      } catch (error) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    });
  };

  const handleOpenDeleteConfirm = async (panelId: string): Promise<void> => {
    await deletePanelFormik.setFieldValue('panelId', panelId);
    setConfirmPanelDialogOpen(true);
  };

  const handleOpenDetail =
    (panelNo: string): (() => void) =>
    () => {
      if (panelNo) navigate(`/internal/gs-homepage/gspanel/details/${panelNo}`);
    };

  const createPanel = useCreatePanel();
  const handleCreatePanel = async (values: PanelFormData): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      const {
        title,
        fundingSource,
        entryYear,
        panelType,
        endDate,
        panelMember,
        tagOptions,
        status,
      } = values;

      const processedPanelMember = panelMember.map(member => {
        const { order, action, name, ...other } = member;
        return { ...other };
      });
      try {
        const variables = {
          input: {
            title,
            fundingSource,
            entryYear,
            panelType,
            // 2024/12/18 remove startDate and endDate, display a field named meeting date, remove gs helper
            // in case it changes again in the future and the api doesn't change.
            // still keep the startDate and endDate fields and pass the same value to the backend
            startDate: dayjs(endDate).toDate(),
            endDate: dayjs(endDate).toDate(),
            panelChair: processedPanelMember?.[0],
            panelMembers: processedPanelMember,
            tagOptions,
            gsHelper: '',
            status,
          },
        };

        await createPanel({
          variables,
        });
        setAlertDetails({
          alertContent: PANEL_LIST_TEXT.CREATE_PANEL_SUCCESS,
          alertType: 'success',
        });
        setPanelDialogOpen(false);
        refetch();
      } catch (error: any) {
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });

    setOpenAlert(true);
  };

  const deletePanel = useDeletePanel();
  const handleDeletePanel = async (): Promise<void> => {
    setConfirmPanelDialogOpen(false);
    await handlePageLoading(setPageLoading, async () => {
      try {
        const variables = {
          input: {
            _id: deletePanelFormik.values.panelId,
          },
        };
        await deletePanel({
          variables,
        });
        setAlertDetails({
          alertContent: PANEL_LIST_TEXT.DELETE_PANEL_SUCCESS,
          alertType: 'success',
        });
        refetch();
      } catch (error: any) {
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const updatePanel = useUpdatePanel();
  const handleUpdatePanel = async (values: PanelFormData): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      const {
        _id,
        title,
        fundingSource,
        entryYear,
        panelType,
        endDate,
        panelMember,
        tagOptions,
      } = values;

      const processedPanelMember = panelMember.map(member => {
        const { order, action, name, ...other } = member;
        return { ...other };
      });

      try {
        const variables = {
          input: {
            _id,
            title,
            fundingSource,
            entryYear,
            panelType,
            startDate: dayjs(endDate).toDate(),
            endDate: dayjs(endDate).toDate(),
            panelChair: processedPanelMember?.[0],
            panelMembers: processedPanelMember,
            tagOptions,
          },
        };
        await updatePanel({
          variables,
        });
        setAlertDetails({
          alertContent: PANEL_LIST_TEXT.SAVE_PANEL_SUCCESS,
          alertType: 'success',
        });
        refetch();
      } catch (error: any) {
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
    setPanelDialogOpen(false);
    setOpenAlert(true);
  };

  const handleSubmitPanel = async (values: PanelFormData): Promise<void> => {
    switch (panelFormMode) {
      case PANEL_FORM_MODE.CREATE:
        await handleCreatePanel(values);
        break;
      case PANEL_FORM_MODE.UPDATE:
        await handleUpdatePanel(values);
        setPanelFormMode(PANEL_FORM_MODE.UPDATE);
        break;
      default:
        setPanelDialogOpen(false);
        break;
    }
  };

  const handleOpenCreatePanel = (): void => {
    setPanelFormData(initialPanelFormData);
    setPanelFormMode(PANEL_FORM_MODE.CREATE);
    setPanelDialogOpen(true);
  };

  const handleClosePanelForm = (): void => {
    setPanelDialogOpen(false);
  };

  if (getGSApplicationsError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (getGSApplicationsError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <Container>
      {pageloading && <Loading />}

      <Dialog
        sx={{
          bgcolor: '#FAFAFA',
        }}
        openDialog={confirmDialogOpen}
        dialogTitle={PANEL_LIST_TEXT.PANEL_DELETE.TITLE}
        dialogAction={PANEL_FORM_DIALOG_TEXT.CONFIRM}
        cancelAction={PANEL_FORM_DIALOG_TEXT.CANCEL}
        onDialogAction={handleDeletePanel}
        width="680px"
        onDialogClose={() => {
          setConfirmPanelDialogOpen(false);
        }}
      >
        <div className="bg-white border border-[#DFDFDF] mx-4 flex flex-col justify-center items-center p-6">
          <p className="font-semibold">
            {PANEL_LIST_TEXT.PANEL_DELETE.CONTENT}
          </p>
        </div>
      </Dialog>
      {loading ? (
        <Loading />
      ) : (
        <div>
          <form noValidate onSubmit={deletePanelFormik.handleSubmit}>
            <Table
              headCells={dataCells}
              data={visibleData}
              handleRequestSort={() => {}}
            />
          </form>
          <div className="pagination-area">
            <Button
              onClick={handleOpenCreatePanel}
              color="primary"
              sx={{
                textTransform: 'none',
              }}
              startIcon={<AddCircleOutline />}
            >
              {PANEL_LIST_TEXT.FORM_PANEL}
            </Button>
          </div>
        </div>
      )}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {panelDialogOpen && (
        <PanelFormDialog
          initialValues={panelFormData}
          fundingsNeedPanel={fundingsNeedPanel}
          open={panelDialogOpen}
          entryYears={entryYears}
          panelFormMode={panelFormMode}
          submitPanel={handleSubmitPanel}
          onClose={handleClosePanelForm}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  h1 {
    text-align: center;
    font-size: 2rem;
    margin: 2rem 0;
  }

  .pagination-area {
    position: sticky;
    bottom: 0;
    display: flex;
    justify-content: space-between;
    text-align: center;
    bottom: 0;
    width: 100%;
    padding: 0.5rem 0;
    background-color: #ffffff;
    margin-bottom: 10rem;
  }
`;
